import { GridColDef } from "@mui/x-data-grid";
import { GridApiPro, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { IDashboardSettings } from "components/preferences";
import { IColumnSetting } from "models/grids";
import { SetPreferenceSignature } from "hooks/preferences";
import { useEffect, useState } from "react";

type UseGridColumnsProps = {
    columns: GridColDef[],
    settings?: IDashboardSettings | null,
    setSettings?: SetPreferenceSignature,
};

export type ColumnVisibilityOrOrderChangeHandler = (params: React.MutableRefObject<GridApiPro>, model?: GridColumnVisibilityModel) => void;

type UseGridColumnsResult = [
    GridColDef[],
    GridColumnVisibilityModel,
    ColumnVisibilityOrOrderChangeHandler,
];

type UseGridColumnsSignature = (props: UseGridColumnsProps) => UseGridColumnsResult;

const useGridColumns: UseGridColumnsSignature = (props) => {
    const { columns, settings, setSettings } = props;
    const [visibilityModel, setVisibilityModel] = useState<GridColumnVisibilityModel>({});
    const [columnsModel, setColumnsModel] = useState<GridColDef[]>(columns);

    useEffect(() => {
        if (!settings) return;
        let gridSettings = settings?.gridSettings ?? [];
        let newColumns: GridColDef[] = [];
        let newVisibility: GridColumnVisibilityModel = {};
        newColumns.push(...columns);

        //ordering
        if (gridSettings.length > 0) {
            newColumns.sort((a, b) => {
                const aIndex = gridSettings?.findIndex(c => c.columnName === a.field);
                const bIndex = gridSettings?.findIndex(c => c.columnName === b.field);

                if (aIndex === -1 || bIndex === -1) return 0;

                return (gridSettings[aIndex].columnOrder ?? 0) - (gridSettings[bIndex].columnOrder ?? 0)
            });
        }

        //visibilitiModel for initialState
        for (let column of columns ?? []) {
            newVisibility[column.field] = gridSettings.find(c => c.columnName == column.field)?.visible ?? true;
        }
        for (let column of newColumns ?? []) {
            column.width = gridSettings.find(c => c.columnName == column.field)?.columnWidth ?? column.width;
        }

        setVisibilityModel(newVisibility);
        setColumnsModel(newColumns);
    }, [columns, settings]);


    const handleColumnVisibilityOrOrderChange: ColumnVisibilityOrOrderChangeHandler = (apiRef: React.MutableRefObject<GridApiPro>, model?: GridColumnVisibilityModel) => {
        let gridSettings: IColumnSetting[] = [];

        const allColumns = apiRef.current.getAllColumns();
        const visibleColumns = apiRef.current.getVisibleColumns();

        let ordinal = 0;

        if (model) {
            setVisibilityModel(model);
            allColumns.forEach(column => {
                gridSettings.push({
                    columnName: column.field,
                    columnOrder: ordinal,
                    visible: model[column.field],
                    columnWidth: column.width ?? 200
                });
                ordinal++;
            });
        }
        else {
            allColumns.forEach(column => {
                gridSettings.push({
                    columnName: column.field,
                    columnOrder: ordinal,
                    visible: visibleColumns.findIndex(f => f.field == column.field) != -1,
                    columnWidth: column.width ?? 200
                });
                ordinal++;
            });
        }
        
        if (setSettings && settings) {
            setSettings({
                ...settings,
                gridSettings: gridSettings
            })
        }

    }

    return [
        columnsModel,
        visibilityModel,
        handleColumnVisibilityOrOrderChange
    ];
};

export default useGridColumns;